/**
 * ClearanceWizard.js
 *
 * Main wizard container component that manages the multi-step clearance flow.
 * Uses Ant Design Steps component for navigation.
 *
 * Navigation Rules:
 * - Step 1 (Student Info) → Step 2 (Select Items): Always allowed
 * - Step 2 → Step 1: Allowed if not confirmed
 * - Step 2 → Step 3 (Review): Allowed after saving selections
 * - Step 3 → Step 2: Allowed if status is "Items Selected" (not confirmed)
 * - Step 3 → Step 4 (Payment): After confirmation (cannot go back)
 * - Step 4: Cannot go back after payment initiated
 *
 * @component
 */
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Steps, Card, Button, Space, Spin, message } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import Utilities from "../../../../dependencies/custom/react-utilities";
import domainSettings from "../../../../dependencies/custom/settings";
import StudentInfoStep from "./StudentInfoStep";
import ItemSelectionStep from "./ItemSelectionStep";
import ReviewConfirmStep from "./ReviewConfirmStep";
import PaymentStep from "./PaymentStep";
import ClearanceSummary from "./ClearanceSummary";

/**
 * Wizard step definitions
 */
const STEPS = [
  { title: "Student Info", description: "Review details" },
  { title: "Select Items", description: "Choose items" },
  { title: "Review & Confirm", description: "Verify selections" },
  { title: "Payment", description: "Complete payment" },
];

/**
 * Maps clearance status to the appropriate wizard step
 * @param {string} status - Clearance status from API
 * @returns {number} Step index (0-3)
 */
const getStepFromStatus = (status) => {
  switch (status) {
    case "Pending Selection":
      return 0;
    case "Items Selected":
      return 2; // Go to Review step
    case "Pending Payment":
    case "Partially Paid":
      return 3;
    case "Paid":
    case "Ready for Pickup":
    case "Partially Collected":
    case "Collected":
    case "Cleared":
      return 4; // Summary view (beyond steps)
    default:
      return 0;
  }
};

/**
 * Determines if user can navigate back from current step
 * @param {number} currentStep - Current step index
 * @param {string} status - Clearance status
 * @returns {boolean}
 */
const canGoBack = (currentStep, status) => {
  // Cannot go back from payment step once confirmed
  if (
    [
      "Pending Payment",
      "Partially Paid",
      "Paid",
      "Ready for Pickup",
      "Cleared",
    ].includes(status)
  ) {
    return false;
  }
  // Can go back from steps 1, 2, and 3 (before confirmation)
  return currentStep > 0;
};

/**
 * ClearanceWizard Component
 *
 * @param {object} assignment - The clearance assignment data
 * @param {function} onBack - Callback to return to assignments list
 */
const ClearanceWizard = ({ assignment, onBack }) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [clearance, setClearance] = useState(null);
  const [loading, setLoading] = useState(true);

  /**
   * Fetch full clearance details including requirement items
   */
  const fetchClearanceDetails = useCallback(async () => {
    if (!assignment?.botpc) return;

    setLoading(true);
    try {
      // Fetch clearance via portal endpoint (includes requirement items)
      const response = await Utilities.fetchWithAuth(
        "GET",
        `${domainSettings.urls.bot.portalClearance}?botpc=${assignment.botpc}`,
        null,
        navigate,
      );

      if (response.ok) {
        const clearanceData = response.data;

        // Fetch requirement items if not included
        if (
          clearanceData.requirement_template &&
          !clearanceData.requirement_items
        ) {
          try {
            const itemsResponse = await Utilities.fetchWithAuth(
              "GET",
              `${domainSettings.urls.bot.requirementItems}?requirement_template=${clearanceData.requirement_template}`,
              null,
              navigate,
            );

            if (itemsResponse.ok) {
              const requirementItems =
                itemsResponse.data.results || itemsResponse.data || [];

              // Fetch full item details (with brands and sizes)
              const itemsWithDetails = await Promise.all(
                requirementItems.map(async (reqItem) => {
                  try {
                    const itemDetailResponse = await Utilities.fetchWithAuth(
                      "GET",
                      domainSettings.urls.bot.itemDetail.replace(
                        ":id",
                        reqItem.bot_item,
                      ),
                      null,
                      navigate,
                    );

                    if (itemDetailResponse.ok) {
                      const itemDetail = itemDetailResponse.data;
                      return {
                        ...reqItem,
                        available_brands: itemDetail.brands || [],
                        available_sizes: itemDetail.size_variants || [],
                        has_size: itemDetail.has_size,
                        has_brands: itemDetail.has_brands,
                      };
                    }
                    return reqItem;
                  } catch (error) {
                    console.error(
                      `Error fetching details for item ${reqItem.bot_item}:`,
                      error,
                    );
                    return reqItem;
                  }
                }),
              );

              clearanceData.requirement_items = itemsWithDetails;
            }
          } catch (error) {
            console.error("Error fetching requirement items:", error);
          }
        }

        setClearance(clearanceData);

        // Set initial step based on status
        const initialStep = getStepFromStatus(clearanceData.status);
        setCurrentStep(initialStep);
      } else {
        Utilities.showError(response, "Failed to load clearance details");
      }
    } catch (error) {
      console.error("Error fetching clearance:", error);
      message.error("Failed to load clearance details");
    } finally {
      setLoading(false);
    }
  }, [assignment, navigate]);

  useEffect(() => {
    fetchClearanceDetails();
  }, [fetchClearanceDetails]);

  /**
   * Handle navigation to next step
   */
  const handleNext = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    }
  };

  /**
   * Handle navigation to previous step
   */
  const handlePrevious = () => {
    if (canGoBack(currentStep, clearance?.status)) {
      setCurrentStep(currentStep - 1);
    }
  };

  /**
   * Handle step click (for direct navigation via Steps component)
   */
  const handleStepClick = (step) => {
    // Can only go backwards if allowed
    if (step < currentStep && canGoBack(currentStep, clearance?.status)) {
      setCurrentStep(step);
    }
    // Can only go forward if status allows
    if (step > currentStep) {
      // Check if we can proceed based on current status
      const statusStep = getStepFromStatus(clearance?.status);
      if (step <= statusStep) {
        setCurrentStep(step);
      }
    }
  };

  /**
   * Refresh clearance data after an action
   */
  const handleRefresh = async () => {
    await fetchClearanceDetails();
  };

  /**
   * Render the current step content
   */
  const renderStepContent = () => {
    // Show summary for completed clearances
    if (
      currentStep >= 4 ||
      [
        "Paid",
        "Ready for Pickup",
        "Partially Collected",
        "Collected",
        "Cleared",
      ].includes(clearance?.status)
    ) {
      return <ClearanceSummary clearance={clearance} onBack={onBack} />;
    }

    switch (currentStep) {
      case 0:
        return <StudentInfoStep clearance={clearance} onNext={handleNext} />;
      case 1:
        return (
          <ItemSelectionStep
            clearance={clearance}
            onNext={handleNext}
            onPrevious={handlePrevious}
            onRefresh={handleRefresh}
            canGoBack={canGoBack(currentStep, clearance?.status)}
          />
        );
      case 2:
        return (
          <ReviewConfirmStep
            clearance={clearance}
            onNext={handleNext}
            onPrevious={handlePrevious}
            onRefresh={handleRefresh}
            canGoBack={canGoBack(currentStep, clearance?.status)}
          />
        );
      case 3:
        return (
          <PaymentStep
            clearance={clearance}
            onRefresh={handleRefresh}
            onBack={onBack}
          />
        );
      default:
        return null;
    }
  };

  /**
   * Get step status for Steps component
   */
  const getStepStatus = (stepIndex) => {
    const statusStep = getStepFromStatus(clearance?.status);

    if (stepIndex < currentStep) return "finish";
    if (stepIndex === currentStep) return "process";
    if (stepIndex <= statusStep) return "finish";
    return "wait";
  };

  if (loading) {
    return (
      <Card>
        <div style={{ textAlign: "center", padding: "60px" }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>Loading clearance details...</div>
        </div>
      </Card>
    );
  }

  // Show summary view for completed statuses
  const isCompleted = [
    "Paid",
    "Ready for Pickup",
    "Partially Collected",
    "Collected",
    "Cleared",
  ].includes(clearance?.status);

  return (
    <div>
      {/* Back Button */}
      <Button
        type="link"
        icon={<ArrowLeftOutlined />}
        onClick={onBack}
        style={{ marginBottom: 16, padding: 0 }}
      >
        Back to Students
      </Button>

      <Card>
        {/* Steps Navigation */}
        {!isCompleted && (
          <Steps
            current={currentStep}
            items={STEPS.map((step, index) => ({
              title: step.title,
              description: step.description,
              status: getStepStatus(index),
              disabled: index > getStepFromStatus(clearance?.status),
            }))}
            onChange={handleStepClick}
            style={{ marginBottom: 32 }}
            responsive={false}
          />
        )}

        {/* Step Content */}
        {renderStepContent()}
      </Card>
    </div>
  );
};

export default ClearanceWizard;
